import Phaser from "phaser";

export default class CreditsScene extends Phaser.Scene {
  constructor() {
    super("CreditsScene");
  }

  preload() {
    this.load.image("titleBG", "/assets/titleBG.png");
    this.load.audio("titleMusic", "/assets/titleScreen.mp3");
    this.load.audio("tap", "/assets/tap.m4a");
    this.load.audio("selected", "/assets/selected.m4a");
  }

  create() {
    const lang = localStorage.getItem("agritech-language") || "es";
    const t = {
      en: {
        credits: "Credits",
        leadGameDeveloper: "Lead Game Developer and Programmer",
        artist: "Artist",
        conceptualizationAndEditing: "Conceptualization and Editing",
        projectOrganizations: "Project Organizations",
        executingOrganization: "Executing organization",
        fundingOrganization: "Funding organization",
        projectPartner: "Project partner",
        innovationEcosystem: "Innovation ecosystem",
        soundAndMusicCredits: "Sound and Music Credits",
        return: "Return",
      },
      es: {
        credits: "Créditos",
        leadGameDeveloper: "Desarrollador principal y programador",
        artist: "Artista",
        conceptualizationAndEditing: "Conceptualización y edición",
        projectOrganizations: "Entidades del proyecto",
        executingOrganization: "Entidad ejecutora",
        fundingOrganization: "Entidad financiadora",
        projectPartner: "Entidad aliada",
        innovationEcosystem: "Ecosistema de innovación",
        soundAndMusicCredits: "Créditos de sonido y música",
        return: "Volver",
      },
    };

    const text = t[lang] || t.en;

    this.add.image(400, 300, "titleBG").setDisplaySize(800, 600);
    this.add.rectangle(400, 300, 800, 600, 0x06130c, 0.62).setDepth(1);

    this.add
      .text(400, 52, text.credits, {
        fontFamily: '"Russo One", sans-serif',
        fontSize: "42px",
        color: "#ecfccb",
        stroke: "#14532d",
        strokeThickness: 4,
        shadow: {
          offsetX: 0,
          offsetY: 3,
          color: "#020d07",
          blur: 6,
          fill: true,
        },
      })
      .setOrigin(0.5)
      .setDepth(2);

    this.add
      .rectangle(400, 315, 660, 420, 0x18231d, 0.94)
      .setStrokeStyle(3, 0x769b55)
      .setDepth(2);

    const creditSections = [
      {
        title: text.projectOrganizations,
        lines: [
          {
            label: `${text.executingOrganization}: Universidad CES`,
            url: "https://www.ces.edu.co/",
          },
          {
            label: `${text.fundingOrganization}: Ruta N`,
            url: "https://rutanmedellin.org/",
          },
          {
            label: `${text.projectPartner}: Jardín Botánico de Medellín`,
            url: "https://www.botanicomedellin.org/",
          },
          {
            label: `${text.innovationEcosystem}: FutuMed (ZTE)`,
            url: "https://rutanmedellin.org/en/futumed",
          },
        ],
      },
      {
        title: text.conceptualizationAndEditing,
        lines: ["Erika Juliana Obando Montoya", "Paola Andrea Zapata"],
      },
      {
        title: text.leadGameDeveloper,
        lines: [
          {
            label: "Luis Carlos Beltrán",
            url:
              lang === "es"
                ? "https://beltranlab.netlify.app/index-es"
                : "https://beltranlab.netlify.app/",
          },
        ],
      },

      {
        title: text.artist,
        lines: [
          {
            label: "Melissa Ospina",
            url:
              lang === "es"
                ? "https://www.instagram.com/melospinam/"
                : "https://www.instagram.com/melospinam/",
          },
        ],
      },
      {
        title: text.soundAndMusicCredits,
        lines: [
          "Music: Maksym Malko (Pixabay)",
          "Power Up 1 SFX: Eschwabe3 (freesound.org/s/460131)",
          "Power Up 2 SFX: Eschwabe3 (freesound.org/s/460132)",
          "Mud Splash: adiantheman (CC0)",
          "Water Splash: lwdickens (CC BY-NC 4.0)",
          "Thunderclap SFX: freesound_community (Pixabay)",
          "Apply SFX: colorsCrimsonTears (freesound.org/s/577961)",
          "Plant Bit SFX: Kenneth_Cooney (freesound.org/s/609335)",
          "Results Music: SterlingRay (freesound.org/s/718481)",
          "Complete SFX: Soundholder (freesound.org/s/425356)",
          "Correct SFX: Kauã Silbershlach Parodes (Pixabay)",
          "Incorrect SFX: freesound_community (Pixabay)",
          "Tap SFX: Lesiakower (Pixabay)",
          "Selected SFX: Kauã Silbershlach Parodes (Pixabay)",
          "Tutorial Music: Pixel Turbocharge https://elevenlabs.io/music/video-game",
        ],
      },
    ];

    const creditsMaskShape = this.add
      .rectangle(400, 315, 610, 350, 0xffffff, 0)
      .setVisible(false);

    const creditsMask = creditsMaskShape.createGeometryMask();

    const creditsContainer = this.add.container(0, 0).setDepth(3);
    creditsContainer.setMask(creditsMask);

    let y = 150;

    creditSections.forEach((section) => {
      creditsContainer.add(
        this.add
          .text(400, y, section.title, {
            fontFamily: '"Montserrat", sans-serif',
            fontSize: "17px",
            color: "#a3e635",
            fontStyle: "bold",
            align: "center",
          })
          .setOrigin(0.5, 0),
      );

      y += 26;

      section.lines.forEach((line) => {
        const label = typeof line === "string" ? line : line.label;
        const url = typeof line === "string" ? null : line.url;

        const lineText = this.add
          .text(400, y, label, {
            fontFamily: '"Montserrat", sans-serif',
            fontSize: "15px",
            color: "#f7faf4",
            align: "center",
            wordWrap: { width: 590 },
          })
          .setOrigin(0.5, 0);

        if (url) {
          lineText.setInteractive({ useHandCursor: true });

          lineText.on("pointerover", () => {
            lineText.setColor("#d9f99d");
          });

          lineText.on("pointerout", () => {
            lineText.setColor("#f7faf4");
          });

          lineText.on("pointerdown", () => {
            window.open(url, "_blank", "noopener,noreferrer");
          });
        }

        creditsContainer.add(lineText);
        y += 22;
      });

      y += 16;
    });

    const minY = 0;
    const maxY = Math.min(0, 500 - y);

    this.input.on("wheel", (pointer, gameObjects, deltaX, deltaY) => {
      creditsContainer.y = Phaser.Math.Clamp(
        creditsContainer.y - deltaY * 0.35,
        maxY,
        minY,
      );
    });

    const backButton = this.add
      .rectangle(400, 570, 220, 46, 0x0b1510, 0.95)
      .setStrokeStyle(2, 0x6f8f5b)
      .setDepth(4)
      .setInteractive({ useHandCursor: true });

    const backText = this.add
      .text(400, 570, text.return, {
        fontFamily: '"Montserrat", sans-serif',
        fontSize: "22px",
        color: "#f8fafc",
        fontStyle: "bold",
      })
      .setOrigin(0.5)
      .setDepth(5);

    backButton.on("pointerover", () => {
      this.sound.play("tap", { volume: 0.45 });
      backButton.setFillStyle(0x18291f, 1);
      backText.setColor("#d9f99d");
    });

    backButton.on("pointerout", () => {
      backButton.setFillStyle(0x0b1510, 0.95);
      backText.setColor("#f8fafc");
    });

    backButton.on("pointerdown", () => {
      this.sound.play("selected", { volume: 0.5 });

      this.scene.stop("CreditsScene");
      this.scene.resume("TitleScene");
    });
  }
}
